import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import IPButton from '../../../../../components/IPButton'
import IPCheckBox from '../../../../../components/IPCheckBox'
import IPSelectBox from '../../../../../components/IPSelectBox'
import InputCampaign from '../../../../../components/InputCampaign'
import { popSelection, pushSelection } from '../drilldownModel'
import { exportManagementReport, ReportPeriod } from '../managementReportExcel'
import { IChatManagementDrilldown, IChatManagementReport, IDrillSelection, IQuestionPage } from '../models'
import { GetChatbotManagementDetailApi } from '../services/getChatbotManagementDetailApi'
import { GetChatbotManagementReportApi } from '../services/getChatbotManagementReportApi'
import { endExclusiveOfLocalDay, formatLocalDate, fromInputDate, startOfLocalDay, toInputDate } from '../viewModel'
import { GetChatbotManagementQuestionsApi } from '../services/getChatbotManagementQuestionsApi'
import DrilldownPanel from './DrilldownPanel'
import ManagementReportTab from './ManagementReportTab'
import OutcomeDefinitionsTab from './OutcomeDefinitionsTab'
import styles from './ChatbotManagementReport.module.scss'
import { DEALER_TYPE_OPTIONS, DealerTypeFilter } from '../../ChatbotAnalytics/analyticsQuery'

type LoadState = 'loading' | 'ready' | 'empty' | 'error'
type ReportTab = 'report' | 'definitions'

interface DateRange {
  start: Date
  end: Date
}

export type ManagementReportLoader = (
  fromUtc: Date,
  toUtc: Date,
  includeTestDealers: boolean,
  dealerType?: DealerTypeFilter
) => Promise<IChatManagementReport>
export type ManagementDetailLoader = (
  fromUtc: Date,
  toUtc: Date,
  path: IDrillSelection[],
  includeTestDealers: boolean,
  dealerType?: DealerTypeFilter
) => Promise<IChatManagementDrilldown>
export type ManagementQuestionsLoader = (
  fromUtc: Date,
  toUtc: Date,
  path: IDrillSelection[],
  page: number,
  includeTestDealers: boolean,
  dealerType?: DealerTypeFilter
) => Promise<IQuestionPage>
export type ManagementReportExporter = (report: IChatManagementReport, period: ReportPeriod) => void

interface ChatbotManagementReportProps {
  loadReport?: ManagementReportLoader
  exportReport?: ManagementReportExporter
  loadDetail?: ManagementDetailLoader
  loadQuestions?: ManagementQuestionsLoader
}

const MAX_RANGE_DAYS = 90
const DAY_MS = 24 * 60 * 60 * 1000
/** Rapor henüz yüklenmediyse (ya da hata aldıysa) başlıkta gösterilen sabit not; yüklenince rapordaki değer kullanılır. */
const DEFAULT_TIME_ZONE_NOTE = 'Türkiye saati (UTC+03:00)'

const loadReportFromApi: ManagementReportLoader = (fromUtc, toUtc, includeTestDealers, dealerType) =>
  new GetChatbotManagementReportApi({ screenCode: 'ChatbotManagementReport' }).getReport(
    fromUtc,
    toUtc,
    includeTestDealers,
    dealerType
  )

const loadDetailFromApi: ManagementDetailLoader = (fromUtc, toUtc, path, includeTestDealers, dealerType) =>
  new GetChatbotManagementDetailApi({ screenCode: 'ChatbotManagementReport' }).getDetail(
    fromUtc,
    toUtc,
    path,
    includeTestDealers,
    dealerType
  )

const loadQuestionsFromApi: ManagementQuestionsLoader = (fromUtc, toUtc, path, page, includeTestDealers, dealerType) =>
  new GetChatbotManagementQuestionsApi({ screenCode: 'ChatbotManagementReport' }).getQuestions(
    fromUtc,
    toUtc,
    path,
    page,
    includeTestDealers,
    dealerType
  )

const createInitialRange = (): DateRange => {
  const end = startOfLocalDay(new Date())
  const start = new Date(end)
  start.setDate(start.getDate() - 13)
  return { start, end }
}

const tabs: { key: ReportTab; label: string }[] = [
  { key: 'report', label: 'Yönetim Raporu' },
  { key: 'definitions', label: 'Yanıt Türleri' },
]

export default function ChatbotManagementReport({
  loadReport = loadReportFromApi,
  exportReport = exportManagementReport,
  loadDetail = loadDetailFromApi,
  loadQuestions = loadQuestionsFromApi,
}: ChatbotManagementReportProps) {
  const initialRangeRef = useRef<DateRange | null>(null)
  if (!initialRangeRef.current) initialRangeRef.current = createInitialRange()

  const [draftStart, setDraftStart] = useState(() => toInputDate(initialRangeRef.current!.start))
  const [draftEnd, setDraftEnd] = useState(() => toInputDate(initialRangeRef.current!.end))
  const [rangeError, setRangeError] = useState<string | null>(null)
  const [appliedRange, setAppliedRange] = useState<DateRange>(initialRangeRef.current)
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [report, setReport] = useState<IChatManagementReport | null>(null)
  const [activeTab, setActiveTab] = useState<ReportTab>('report')
  // Deneme kartı (ADR-022 §5): varsayılan hariç; değişince rapor hemen yeniden yüklenir.
  const [includeTestDealers, setIncludeTestDealers] = useState(false)
  // Bayi Tipi (Analitik sayfasıyla aynı: Tüm / Vodafone / Normal). Ön yüz bayi kodu tutmaz, yalnız seçimi gönderir;
  // rapor, detay ve soru listesi aynı seçimle okunur. Değişince rapor hemen yeniden yüklenir.
  const [dealerType, setDealerType] = useState<DealerTypeFilter | undefined>(undefined)
  const requestSequenceRef = useRef(0)
  const mountedRef = useRef(false)
  const [drillStack, setDrillStack] = useState<IDrillSelection[]>([])
  // Detay ucuna giden aralık, ekrandaki raporun aralığıdır; Date kimliği sabit kalsın diye memo'lanır.
  const appliedUtc = useMemo(
    () => ({ from: startOfLocalDay(appliedRange.start), to: endExclusiveOfLocalDay(appliedRange.end) }),
    [appliedRange]
  )
  const openDrill = useCallback((selection: IDrillSelection) => setDrillStack([selection]), [])
  const navigateDrill = useCallback(
    (selection: IDrillSelection) => setDrillStack(stack => pushSelection(stack, selection)),
    []
  )
  const backDrill = useCallback(() => setDrillStack(stack => popSelection(stack)), [])
  const closeDrill = useCallback(() => setDrillStack([]), [])
  const detailLoader = useCallback(
    (fromUtc: Date, toUtc: Date, path: IDrillSelection[]): Promise<IChatManagementDrilldown> =>
      dealerType
        ? loadDetail(fromUtc, toUtc, path, includeTestDealers, dealerType)
        : loadDetail(fromUtc, toUtc, path, includeTestDealers),
    [loadDetail, includeTestDealers, dealerType]
  )
  const questionsLoader = useCallback(
    (fromUtc: Date, toUtc: Date, path: IDrillSelection[], page: number): Promise<IQuestionPage> =>
      dealerType
        ? loadQuestions(fromUtc, toUtc, path, page, includeTestDealers, dealerType)
        : loadQuestions(fromUtc, toUtc, path, page, includeTestDealers),
    [loadQuestions, includeTestDealers, dealerType]
  )

  const load = useCallback(
    async (range: DateRange, withTestDealers: boolean, type?: DealerTypeFilter) => {
      const requestSequence = ++requestSequenceRef.current
      setLoadState('loading')

      try {
        const from = startOfLocalDay(range.start)
        const to = endExclusiveOfLocalDay(range.end)
        // Tüm bayiler seçiliyken eski çağrı biçimi korunur (parametre hiç gönderilmez).
        const response = type
          ? await loadReport(from, to, withTestDealers, type)
          : await loadReport(from, to, withTestDealers)
        if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return

        setReport(response)
        setLoadState(response.Kpis.TotalTurns === 0 ? 'empty' : 'ready')
      } catch {
        if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return

        setReport(null)
        setLoadState('error')
      }
    },
    [loadReport]
  )

  useEffect(() => {
    mountedRef.current = true
    load(initialRangeRef.current!, false)

    return () => {
      mountedRef.current = false
      requestSequenceRef.current += 1
    }
  }, [load])

  const applyDates = () => {
    const start = fromInputDate(draftStart)
    const end = fromInputDate(draftEnd)

    if (!start || !end) {
      setRangeError('Geçerli bir başlangıç ve bitiş tarihi seçin.')
      return
    }
    if (start > end) {
      setRangeError('Başlangıç tarihi bitiş tarihinden sonra olamaz.')
      return
    }
    if (Math.round((endExclusiveOfLocalDay(end).getTime() - start.getTime()) / DAY_MS) > MAX_RANGE_DAYS) {
      setRangeError('En fazla 90 günlük aralık seçilebilir.')
      return
    }

    setRangeError(null)
    setDrillStack([])
    const nextRange = { start, end }
    setAppliedRange(nextRange)
    load(nextRange, includeTestDealers, dealerType)
  }

  const toggleTestDealers = (checked: boolean) => {
    setIncludeTestDealers(checked)
    setDrillStack([])
    load(appliedRange, checked, dealerType)
  }

  /** Bayi tipi "Uygula"yı beklemez: deneme kartı kutusuyla aynı davranış. */
  const changeDealerType = (type?: DealerTypeFilter) => {
    setDealerType(type)
    setDrillStack([])
    load(appliedRange, includeTestDealers, type)
  }

  const exportToExcel = () => {
    if (!report || loadState !== 'ready') return
    exportReport(report, { start: toInputDate(appliedRange.start), end: toInputDate(appliedRange.end) })
  }

  const hasData = report !== null && report.Kpis.TotalTurns > 0
  const showContent = hasData && (loadState === 'ready' || loadState === 'loading')

  return (
    <main className={styles.shell} aria-busy={loadState === 'loading'}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Site Raporları</span>
          <h1>B-Gini AI Asistan – Yönetim Raporu</h1>
          <p data-testid='report-period'>
            Rapor dönemi: {formatLocalDate(toInputDate(appliedRange.start))} –{' '}
            {formatLocalDate(toInputDate(appliedRange.end))} · Saat dilimi: {report?.TimeZone ?? DEFAULT_TIME_ZONE_NOTE}
          </p>
          <p>Seçilen dönemde bayi sorularının yanıt türü, başarı ve kullanım dağılımı.</p>
        </div>
        <span className={styles.privacyBadge}>Soru ve yanıtlar ayrı yetkiyle görünür</span>
      </header>

      <section className={styles.filterPanel} aria-label='Rapor aralığı'>
        <div className={styles.filterControls}>
          <div className={styles.dateField}>
            <InputCampaign
              type='date'
              id='management-report-start'
              name='management-report-start'
              label='Başlangıç tarihi'
              value={draftStart}
              max={draftEnd}
              onChange={event => setDraftStart(event.target.value)}
              isActive
            />
          </div>
          <div className={styles.dateField}>
            <InputCampaign
              type='date'
              id='management-report-end'
              name='management-report-end'
              label='Bitiş tarihi'
              value={draftEnd}
              min={draftStart}
              max={toInputDate(new Date())}
              onChange={event => setDraftEnd(event.target.value)}
              isActive
            />
          </div>
          <div className={styles.filterActions}>
            <IPButton text='Uygula' type='button' onClick={applyDates} className={styles.primaryButton} />
            <IPButton
              text='Yenile'
              type='button'
              onClick={() => load(appliedRange, includeTestDealers, dealerType)}
              className={styles.secondaryButton}
            />
            <IPButton
              text="Excel'e Aktar"
              type='button'
              onClick={exportToExcel}
              className={styles.excelButton}
              disabled={loadState !== 'ready'}
            />
          </div>
          <div className={styles.dealerTypeField} data-testid='dealer-type-filter'>
            <IPSelectBox
              id='management-report-dealer-type'
              aria-label='Bayi tipi'
              options={DEALER_TYPE_OPTIONS}
              value={DEALER_TYPE_OPTIONS.find(option => option.value === (dealerType ?? '')) ?? DEALER_TYPE_OPTIONS[0]}
              onChangeSingle={option =>
                changeDealerType(option?.value ? (String(option.value) as DealerTypeFilter) : undefined)
              }
            />
          </div>
          <div className={styles.testDealerToggle} data-testid='test-dealer-toggle'>
            <IPCheckBox
              id='management-report-test-dealers'
              label='Deneme kartı dahil'
              checked={includeTestDealers}
              onCheckedChange={toggleTestDealers}
              removeMarginBottom
            />
          </div>
        </div>
        {rangeError ? (
          <p className={styles.rangeError} role='alert'>
            {rangeError}
          </p>
        ) : null}
      </section>

      <div className={styles.tabs} role='tablist' aria-label='Rapor sekmeleri'>
        {tabs.map(tab => (
          <button
            key={tab.key}
            type='button'
            role='tab'
            data-testid={`tab-${tab.key}`}
            aria-selected={activeTab === tab.key}
            className={activeTab === tab.key ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loadState === 'loading' && !hasData ? (
        <section className={styles.statePanel} role='status' aria-live='polite'>
          <span className={styles.loadingMark} aria-hidden='true' />
          <p>Yönetim raporu hazırlanıyor.</p>
        </section>
      ) : null}

      {loadState === 'error' ? (
        <section className={`${styles.statePanel} ${styles.errorState}`} role='alert'>
          <span className={styles.stateIcon} aria-hidden='true'>
            !
          </span>
          <p>Rapor yüklenemedi. Lütfen tekrar deneyin; sorun sürerse "Chatbot Raporları" yetkinizi kontrol ettirin.</p>
        </section>
      ) : null}

      {loadState === 'empty' ? (
        <section className={styles.statePanel} role='status'>
          <p>Seçilen aralıkta chatbot kullanımı yok.</p>
        </section>
      ) : null}

      {showContent && report ? (
        <div className={loadState === 'loading' ? styles.refreshing : undefined}>
          {activeTab === 'report' ? <ManagementReportTab report={report} onDrill={openDrill} /> : null}
          {activeTab === 'definitions' ? <OutcomeDefinitionsTab report={report} onDrill={openDrill} /> : null}
        </div>
      ) : null}

      <DrilldownPanel
        stack={drillStack}
        fromUtc={appliedUtc.from}
        toUtc={appliedUtc.to}
        loadDetail={detailLoader}
        loadQuestions={questionsLoader}
        onNavigate={navigateDrill}
        onBack={backDrill}
        onClose={closeDrill}
      />
    </main>
  )
}
